import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { recordsApi, workOrdersApi, reportsApi } from "@/lib/api";
import { toast } from "sonner";
import {
  Search,
  FolderOpen,
  User,
  Calendar,
  FileText,
  FlaskConical,
  Download,
  Eye,
  ClipboardList,
  Hash,
} from "lucide-react";

const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-800" },
    in_progress: { label: "En Proceso", className: "bg-blue-100 text-blue-800" },
    completed: { label: "Completado", className: "bg-green-100 text-green-800" },
    delivered: { label: "Entregado", className: "bg-gray-100 text-gray-800" },
  };
  const { label, className } = config[status] || config.pending;
  return <Badge className={className}>{label}</Badge>;
};

// Debounce hook for real-time search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function PatientRecords() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [expedienteRecords, setExpedienteRecords] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showVisitDetail, setShowVisitDetail] = useState(false);

  // Debounced search term for real-time search
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Real-time search effect
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.length < 1) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await recordsApi.searchPatients(debouncedSearchTerm);
        setSearchResults(response.data);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [debouncedSearchTerm]);

  const loadExpedienteRecords = async (expediente, patientName) => {
    setLoadingRecords(true);
    setSelectedExpediente({ expediente, name: patientName });
    try {
      const response = await recordsApi.getExpedienteRecords(expediente);
      setExpedienteRecords(response.data);
    } catch (error) {
      toast.error("Error al cargar expediente");
    } finally {
      setLoadingRecords(false);
    }
  };

  const viewVisitDetail = async (order) => {
    try {
      const response = await workOrdersApi.getFull(order.id);
      setSelectedVisit(response.data);
      setShowVisitDetail(true);
    } catch (error) {
      toast.error("Error al cargar detalle de visita");
    }
  };

  const downloadResultsPdf = async (orderId, orderNumber) => {
    try {
      const response = await reportsApi.downloadResultsPdf(orderId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      // Usar nombre del paciente del expediente seleccionado
      const patientName = selectedExpediente?.name 
        ? selectedExpediente.name.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '').replace(/\s+/g, '_')
        : orderNumber;
      link.setAttribute("download", `resultados_${patientName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al descargar PDF");
    }
  };

  const genderMap = { male: "Masculino", female: "Femenino", other: "Otro" };

// Normaliza referencias: el separador de rango "^" (ej. "70.00^196.0") se muestra como "70.00 - 196.0"
const formatReference = (reference) => {
  if (!reference) return "-";
  return String(reference).replace(/(\d)\s*\^\s*(\d)/g, "$1 - $2");
};


  return (
    <div className="space-y-6" data-testid="patient-records-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Expedientes de Pacientes
        </h1>
        <p className="text-muted-foreground">
          Consulte el historial completo de estudios y resultados por paciente
        </p>
      </div>

      {/* Search Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Search className="h-5 w-5" />
            Buscar Paciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Input
              placeholder="Escriba el nombre o número de expediente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10"
              data-testid="search-patient-input"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            La búsqueda se realiza en tiempo real mientras escribe
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Search Results */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5" />
              Pacientes Encontrados
              {searchResults.length > 0 && (
                <Badge variant="secondary">{searchResults.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {searchTerm ? "No se encontraron pacientes" : "Escriba para buscar pacientes"}
              </p>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {searchResults.map((group, idx) => (
                    <div
                      key={idx}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedExpediente?.expediente === group.expediente
                          ? "bg-primary/10 border-primary"
                          : "hover:bg-muted/50"
                      }`}
                      onClick={() => loadExpedienteRecords(group.expediente, group.name)}
                      data-testid={`expediente-${group.expediente}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Hash className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono font-bold text-primary">
                          {group.expediente}
                        </span>
                      </div>
                      <div className="font-semibold">{group.name}</div>
                      <div className="flex justify-between items-center mt-2 text-xs text-muted-foreground">
                        <span>{genderMap[group.gender] || "-"}</span>
                        <Badge variant="outline">
                          {group.visits?.length || 0} ingreso(s)
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Expediente Records / Visits */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FolderOpen className="h-5 w-5" />
              {selectedExpediente ? (
                <>
                  Expediente: <span className="font-mono text-primary">{selectedExpediente.expediente}</span>
                </>
              ) : (
                "Expediente del Paciente"
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingRecords ? (
              <div className="text-center py-8 text-muted-foreground">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                Cargando expediente...
              </div>
            ) : !expedienteRecords ? (
              <div className="text-center py-8 text-muted-foreground">
                <FolderOpen className="h-12 w-12 mx-auto mb-2 opacity-30" />
                Seleccione un paciente para ver su expediente
              </div>
            ) : expedienteRecords.visits.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Este paciente no tiene visitas registradas
              </div>
            ) : (
              <div>
                {/* Patient Info Summary */}
                <div className="bg-muted/50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Expediente:</span>
                      <p className="font-mono font-bold text-primary">
                        {expedienteRecords.expediente}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Nombre:</span>
                      <p className="font-semibold">
                        {expedienteRecords.patient.first_name} {expedienteRecords.patient.last_name}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Sexo:</span>
                      <p className="font-semibold">
                        {genderMap[expedienteRecords.patient.gender] || "-"}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total Ingresos:</span>
                      <p className="font-semibold">{expedienteRecords.total_visits}</p>
                    </div>
                  </div>
                </div>

                {/* Visits List */}
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Historial de Ingresos
                </h4>
                <ScrollArea className="h-[350px]">
                  <div className="space-y-4">
                    {expedienteRecords.visits.map((visit, visitIdx) => (
                      <div key={visitIdx} className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Folio del día</p>
                            <p className="font-mono font-bold text-lg">{visit.folio}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Fecha de ingreso</p>
                            <p className="font-semibold">
                              {visit.registered_at
                                ? new Date(visit.registered_at).toLocaleDateString("es-MX", {
                                    dateStyle: "medium",
                                  })
                                : "-"}
                            </p>
                          </div>
                        </div>

                        {/* Orders for this visit */}
                        {visit.orders && visit.orders.length > 0 ? (
                          <div className="space-y-2">
                            {visit.orders.map((order) => (
                              <div
                                key={order.id}
                                className="bg-muted/30 rounded p-3"
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <p className="text-sm font-medium">
                                      Orden: {order.order_number}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {order.created_at
                                        ? new Date(order.created_at).toLocaleString("es-MX", {
                                            dateStyle: "short",
                                            timeStyle: "short",
                                          })
                                        : "-"}
                                    </p>
                                  </div>
                                  <StatusBadge status={order.status} />
                                </div>

                                {/* Studies summary */}
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {order.studies?.slice(0, 4).map((study, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {study.study_code || study.study_name}
                                    </Badge>
                                  ))}
                                  {order.studies?.length > 4 && (
                                    <Badge variant="secondary" className="text-xs">
                                      +{order.studies.length - 4} más
                                    </Badge>
                                  )}
                                </div>

                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => viewVisitDetail(order)}
                                    data-testid={`view-order-${order.id}`}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Ver Detalle
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadResultsPdf(order.id, order.order_number)}
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    PDF
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Sin órdenes de trabajo registradas
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Visit Detail Dialog */}
      <Dialog open={showVisitDetail} onOpenChange={setShowVisitDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Detalle de Orden - {selectedVisit?.order_number}
            </DialogTitle>
          </DialogHeader>

          {selectedVisit && (
            <div className="space-y-6">
              {/* Visit Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Folio:</span>
                    <p className="font-bold">{selectedVisit.order_number}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Fecha:</span>
                    <p className="font-semibold">
                      {new Date(selectedVisit.created_at).toLocaleDateString("es-MX")}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total:</span>
                    <p className="font-semibold">${selectedVisit.total?.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Estado:</span>
                    <div className="mt-1">
                      <StatusBadge status={selectedVisit.status} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Studies */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <FlaskConical className="h-4 w-4" />
                  Estudios Solicitados
                </h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Estudio</TableHead>
                      <TableHead>Recipiente</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedVisit.studies?.map((study, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono font-bold">
                          {study.study_code}
                        </TableCell>
                        <TableCell>{study.study_name}</TableCell>
                        <TableCell>{study.container_name || "-"}</TableCell>
                        <TableCell className="text-right">
                          ${study.price?.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Results */}
              {selectedVisit.results && selectedVisit.results.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Resultados
                  </h4>
                  <Accordion type="multiple" className="w-full">
                    {selectedVisit.results.map((result, idx) => (
                      <AccordionItem key={idx} value={`result-${idx}`}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{result.study_name}</span>
                            {result.validated && (
                              <Badge className="bg-green-100 text-green-800">Validado</Badge>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {result.values && result.values.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Analito</TableHead>
                                  <TableHead>Resultado</TableHead>
                                  <TableHead>Unidad</TableHead>
                                  <TableHead>Referencia</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {result.values.map((val, vidx) => (
                                  <TableRow
                                    key={vidx}
                                    className={val.is_abnormal ? "bg-red-50" : ""}
                                  >
                                    <TableCell>{val.analyte_name}</TableCell>
                                    <TableCell
                                      className={`font-semibold ${
                                        val.is_abnormal ? "text-red-600" : ""
                                      }`}
                                    >
                                      {val.value}
                                    </TableCell>
                                    <TableCell>{val.unit || "-"}</TableCell>
                                    <TableCell>{formatReference(val.reference)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Sin resultados capturados
                            </p>
                          )}
                          {result.notes && (
                            <p className="text-sm mt-2 text-muted-foreground">
                              <strong>Notas:</strong> {result.notes}
                            </p>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              {(!selectedVisit.results || selectedVisit.results.length === 0) && (
                <div className="text-center py-4 text-muted-foreground bg-muted/30 rounded-lg">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No hay resultados capturados para esta orden</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
